import React, { useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight, FiCrosshair } from "react-icons/fi";
import "./Setups.css";
import CodexEvaluationCard from "../components/CodexEvaluationCard";
import AssetLabel from "../components/common/AssetLabel";
import { matchesGlobalEventFilters } from "../utils/filters";

const ACTIVE_STATUSES = new Set(["ARMED", "CONFIRMED"]);

// Match Java's market convention: spot is BASE-COIN, futures is COINBASE.
export const isFuturesSetup = (setup) => typeof setup?.market === "string"
  && setup.market.length > 4 && !setup.market.includes("-");

function formatPrice(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
  const numeric = Number(value);
  return Math.abs(numeric) >= 1 ? numeric.toFixed(4) : numeric.toFixed(8);
}

function formatTimeframe(value) {
  return String(value || "").replace(/^_/, "");
}

function relativeTime(epochSeconds) {
  if (!epochSeconds) return "";
  const seconds = Math.max(0, Math.round(Date.now() / 1000 - epochSeconds));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function formatAiLabel(value) {
  return String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function SetupCard({ setup, allSetups, openPopup }) {
  const event = setup?.event;
  const aiEvaluation = setup?.aiEvaluation;
  const hasAiScore = aiEvaluation?.score !== null && aiEvaluation?.score !== undefined;
  const hasAiConfidence = aiEvaluation?.confidence !== null
    && aiEvaluation?.confidence !== undefined
    && (hasAiScore || Number(aiEvaluation.confidence) > 0);
  const openChart = () => {
    if (!event || !openPopup) return;
    const setupsWithEvents = allSetups.filter((item) => item?.event);
    const sectionEvents = setupsWithEvents.map((item) => ({
      ...item.event,
      assetType: item.assetType || item.event.assetType,
      codexTargetId: item.codexTargetId,
      setupChartContext: {
        currency: item.currency,
        direction: item.direction,
        status: item.status,
        sourceLabel: item.sourceLabel,
        triggerTimeframe: item.triggerTimeframe
      }
    }));
    const index = Math.max(0, setupsWithEvents.findIndex((item) => item === setup));
    openPopup({
      index,
      sectionEvents,
      sectionTitleWhenFromHistory: "Setups"
    });
  };

  const directionClass = setup.direction === "LONG" ? "setupLong" : "setupShort";
  const statusClass = `setupStatus setupStatus${setup.status}`;
  const confirmText = setup.confirmPrice == null
    ? "Waiting for reaction"
    : formatPrice(setup.confirmPrice);

  return (
    <button type="button" className={`setupCard ${directionClass}`} onClick={openChart}>
      <div className="setupCardHeader">
        <div className="setupIdentity">
          <strong><AssetLabel symbol={setup.currency} assetType={setup.assetType || event?.assetType} /></strong>
          <span className="setupDirection">{setup.direction}</span>
          <span className={statusClass}>{setup.status}</span>
        </div>
        <span className="setupAge">{relativeTime(setup.confirmedAt || setup.createdAt)}</span>
      </div>

      <div className="setupRoute">
        {setup.sourceLabel} <span aria-hidden="true">-&gt;</span>{" "}
        {formatTimeframe(setup.triggerTimeframe)} reaction
      </div>

      <div className="setupLevels">
        <span><small>Confirm</small>{confirmText}</span>
        <span><small>Invalidation</small>{formatPrice(setup.invalidationPrice)}</span>
        <span><small>Target</small>{formatPrice(setup.targetPrice)}</span>
      </div>

      <div className="setupMetrics">
        <span>{Number(setup.roomPercent || 0).toFixed(2)}% room</span>
        <span>{Number(setup.rewardRisk || 0).toFixed(2)}R</span>
        <span>{formatTimeframe(setup.targetTimeframe)} liquidity</span>
        {setup.btcRegime && <span>BTC {setup.btcRegime}</span>}
        {setup.maxFavorablePercent > 0 && (
          <span>MFE +{Number(setup.maxFavorablePercent).toFixed(2)}%</span>
        )}
        {setup.maxAdversePercent < 0 && (
          <span>MAE {Number(setup.maxAdversePercent).toFixed(2)}%</span>
        )}
        {setup.closePercent !== null && setup.closePercent !== undefined && (
          <span>Close {Number(setup.closePercent) >= 0 ? "+" : ""}{Number(setup.closePercent).toFixed(2)}%</span>
        )}
      </div>

      {setup.statusReason && <div className="setupReason">{setup.statusReason}</div>}

      {aiEvaluation && (
        <div className={`setupAiEvaluation setupAi${aiEvaluation.verdict}`}>
          <div className="setupAiHeader">
            <strong>
              OpenAI evaluation · {formatAiLabel(aiEvaluation.verdict)}
              {hasAiScore ? ` · Score ${aiEvaluation.score}` : ""}
            </strong>
            {hasAiConfidence && <span>Confidence {aiEvaluation.confidence}%</span>}
            <span>{formatAiLabel(aiEvaluation.timing)}</span>
            <small>{aiEvaluation.model}</small>
          </div>
          {aiEvaluation.summary && <p>{aiEvaluation.summary}</p>}
          <div className="setupAiSignals">
            {(aiEvaluation.strengths || []).map((item, index) => (
              <span className="setupAiStrength" key={`${setup.id}-ai-strength-${index}`}>{item}</span>
            ))}
            {(aiEvaluation.risks || []).map((item, index) => (
              <span className="setupAiRisk" key={`${setup.id}-ai-risk-${index}`}>{item}</span>
            ))}
          </div>
        </div>
      )}

      <CodexEvaluationCard latest={setup.codexEvaluation} history={setup.codexEvaluations || []} compact />

      {Array.isArray(setup.evidence) && setup.evidence.length > 0 && (
        <div className="setupEvidence">
          {setup.evidence.slice(0, 4).map((item, index) => (
            <span key={`${setup.id}-evidence-${index}`}>{item}</span>
          ))}
          {setup.evidence.length > 4 && <span>+{setup.evidence.length - 4} more</span>}
        </div>
      )}

      <span className="setupOpenChart">
        <FiCrosshair aria-hidden="true" />
        Chart
      </span>
    </button>
  );
}

function SetupGroup({ title, setups, allSetups, openPopup, collapsed, onToggle }) {
  if (!setups.length) return null;
  return (
    <section className="setupGroup">
      <button
        type="button"
        className="setupGroupHeader"
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        {collapsed ? <FiChevronRight aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
        <span>{title} ({setups.length})</span>
      </button>
      {!collapsed && (
        <div className="setupGrid">
          {setups.map((setup) => (
            <SetupCard
              key={setup.id}
              setup={setup}
              allSetups={allSetups}
              openPopup={openPopup}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function SetupsTab({ snapshot, openPopup, filters = {} }) {
  const [collapsed, setCollapsed] = useState({
    CONFIRMED: false,
    ARMED: false,
    RECENT: true
  });
  // Also hide spot records from snapshots produced before the Java update.
  const allSetups = useMemo(() => (Array.isArray(snapshot?.setups) ? snapshot.setups : [])
    .filter(isFuturesSetup), [snapshot?.setups]);
  const setups = useMemo(() => allSetups.filter((setup) => matchesGlobalEventFilters({
    assetType: setup.assetType || setup.event?.assetType,
    codexEvaluation: setup.codexEvaluation,
  }, {
    cryptoOnly: filters.cryptoOnly,
    aiValidatedOnly: filters.aiValidatedOnly,
    referenceOnly: filters.referenceOnly,
  })), [allSetups, filters.cryptoOnly, filters.aiValidatedOnly, filters.referenceOnly]);
  const groups = useMemo(() => ({
    CONFIRMED: setups.filter((setup) => setup.status === "CONFIRMED"),
    ARMED: setups.filter((setup) => setup.status === "ARMED"),
    RECENT: setups.filter((setup) => !ACTIVE_STATUSES.has(setup.status))
  }), [setups]);

  const toggle = (key) => {
    setCollapsed((current) => ({ ...current, [key]: !current[key] }));
  };

  if (!setups.length) {
    return (
      <div className="setupsEmpty">
        <FiCrosshair aria-hidden="true" />
        <strong>{allSetups.length ? "No setups match these filters" : "No qualified setups yet"}</strong>
        <span>{allSetups.length ? "Adjust the filters above to see more setups."
          : "Raw events remain available in Insights and History."}</span>
      </div>
    );
  }

  return (
    <div className="setupsTab">
      <header className="setupsHeader">
        <div>
          <h2>Setups</h2>
          <p>Qualified event sequences with room to higher-timeframe liquidity.</p>
        </div>
        <div className="setupsSummary">
          <span>{groups.CONFIRMED.length} confirmed</span>
          <span>{groups.ARMED.length} armed</span>
        </div>
      </header>

      <SetupGroup
        title="Confirmed"
        setups={groups.CONFIRMED}
        allSetups={setups}
        openPopup={openPopup}
        collapsed={collapsed.CONFIRMED}
        onToggle={() => toggle("CONFIRMED")}
      />
      <SetupGroup
        title="Armed"
        setups={groups.ARMED}
        allSetups={setups}
        openPopup={openPopup}
        collapsed={collapsed.ARMED}
        onToggle={() => toggle("ARMED")}
      />
      <SetupGroup
        title="Recent outcomes"
        setups={groups.RECENT}
        allSetups={setups}
        openPopup={openPopup}
        collapsed={collapsed.RECENT}
        onToggle={() => toggle("RECENT")}
      />
    </div>
  );
}
